import { useAutoAnimate } from "@formkit/auto-animate/react";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import React, { useMemo, useRef } from "react";
import { z } from "zod";

import { faLinkSimple } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	Alert,
	FormCheckboxGroup,
	FormDatePicker,
	FormHandle,
	FormSubscribe,
	ModalBase,
	PYForm,
	SubmitButton,
	toast,
} from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";
import { ButtonStandard } from "@performyard/py-components";

import { CopyToClipboard } from "../../../../../../components/CopyToClipboard";
import Input from "../../../../../../components/Inputs/InputText";
import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../../../components/Spacer";
import Tooltip from "../../../../../../components/Tooltip";

import { useSetters as useBuilderSetters } from "../../pages/SurveyBuilder/store/store";
import { useSetters as useSurveyParticipantsSetters } from "../../pages/SurveyParticipants/store/store";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import { useModal, useSetters, useSurveyLink, useValidName, useValidQuestions } from "../../store/store";

import { useUpdateSurvey } from "../../../../../../api/hooks";
import { useCompanySettings } from "../../../../../../api/hooks/defs/company";
import { useSurvey } from "../../hooks/useSurvey";

import { formatErrorMessage } from "../../../../../../utils/TanstackQueryErrorHandling";
import { DateNormalizationEffect } from "./utils";

const basePublishSurveyFormSchema = z.object({
	scheduleDates: z.array(z.string()),
	startDate: z.date().nullable().optional(),
	endDate: z.date().nullable().optional(),
});

// Schema factory that accepts companyTimezone for proper date validation
const createPublishSurveyFormSchema = (companyTimezone: string) =>
	basePublishSurveyFormSchema.superRefine((data, ctx) => {
		const hasScheduledDates = data.scheduleDates.includes("schedule-dates");
		// Get current time in company timezone for proper comparison
		const now = utcToZonedTime(new Date(), companyTimezone);

		if (hasScheduledDates) {
			// Start date is required when scheduling
			if (!data.startDate) {
				ctx.addIssue({
					code: "custom",
					message: "Start date is required",
					path: ["startDate"],
				});
			} else if (data.startDate < now) {
				ctx.addIssue({
					code: "custom",
					message: "Start date must be in the future",
					path: ["startDate"],
				});
			}

			// End date validations (only if end date is provided)
			if (data.endDate) {
				if (data.endDate < now) {
					ctx.addIssue({
						code: "custom",
						message: "End date must be in the future",
						path: ["endDate"],
					});
				} else if (data.startDate && data.endDate <= data.startDate) {
					ctx.addIssue({
						code: "custom",
						message: "End date must be after start date",
						path: ["endDate"],
					});
				}
			}
		}
	});

export const PublishSurveyModal = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const modal = useModal();
	const setters = useSetters();
	const builderSetters = useBuilderSetters();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();
	const validQuestions = useValidQuestions();
	const validName = useValidName();

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = useMemo(() => {
		return companySettings?.default_timezone || "America/New_York";
	}, [companySettings?.default_timezone]);

	// Create schema dynamically based on company timezone
	const publishSurveyFormSchema = useMemo(() => createPublishSurveyFormSchema(companyTimezone), [companyTimezone]);

	const formRef = useRef<FormHandle<typeof publishSurveyFormSchema>>(null);

	const onSuccess = () => {
		surveyParticipantsSetters.setSurveyAccess(
			survey?.participantSelection?.enabled ? "selected-employees" : "all-employees"
		);
		setters.setModal(SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_SUCCESS);
	};

	const onError = (error: { data?: { message?: string } }, mutation) => {
		if (error.data?.message?.endsWith("Survey name must be unique")) {
			builderSetters.setInvalidNameMessage("A survey with this name already exists");
		} else {
			const messages = formatErrorMessage(error, mutation);
			messages.forEach((message) => {
				toast.error(message);
			});
		}
	};

	const { mutate: publishSurvey } = useUpdateSurvey(surveyId, { onSuccess, onError });

	const handleSubmit = ({ value }: { value: z.infer<typeof publishSurveyFormSchema> }) => {
		const hasScheduledDates = value.scheduleDates.includes("schedule-dates");

		if (hasScheduledDates) {
			// Convert from company timezone to UTC for storage
			publishSurvey({
				startDate: zonedTimeToUtc(value.startDate!, companyTimezone),
				endDate: value.endDate ? zonedTimeToUtc(value.endDate, companyTimezone) : null,
				scheduleDates: true,
				status: "active",
				name: validName,
				questions: validQuestions,
			});
		} else {
			publishSurvey({
				status: "active",
				startDate: new Date(),
				scheduleDates: false,
				name: validName,
				questions: validQuestions,
				endDate: null,
			});
		}
	};

	const handleClose = () => {
		setters.setModal(null);
		setters.setValidQuestions([]);
		setters.setValidName("");
	};

	const [dateFieldsRef] = useAutoAnimate();

	return (
		<ModalBase isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY} onClose={handleClose}>
			<div className="w-[400px] px-4 pb-6 text-center">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					Publish this survey?
				</Typography>
				<Typography size="paragraph-2" color="neutral-9" weight="regular">
					Published surveys can start accepting responses
				</Typography>
				<Spacer height={2} />
				<PYForm
					ref={formRef}
					schema={publishSurveyFormSchema}
					defaultValues={{
						scheduleDates: [],
						startDate: null,
						endDate: null,
					}}
					onSubmit={handleSubmit}
					id="publish-survey-form"
				>
					{/* Normalize dates when user types invalid times */}
					<FormSubscribe
						id="date-normalization"
						selector={(state: { values: z.infer<typeof publishSurveyFormSchema> }) => ({
							startDate: state.values.startDate,
							endDate: state.values.endDate,
						})}
					>
						{({ startDate, endDate }) => (
							<DateNormalizationEffect
								startDate={startDate}
								endDate={endDate}
								setFieldValue={(name, value) => {
									const formApi = formRef.current as unknown as {
										setFieldValue: (name: string, value: unknown) => void;
									} | null;
									formApi?.setFieldValue(name, value);
								}}
							/>
						)}
					</FormSubscribe>

					<div ref={dateFieldsRef} className="flex flex-col gap-4">
						<FormCheckboxGroup
							id="scheduleDates"
							options={[{ label: "Schedule Start and End Date", value: "schedule-dates" }]}
						/>
						<FormSubscribe
							id="date-fields"
							selector={(state: { values: z.infer<typeof publishSurveyFormSchema> }) => ({
								hasScheduledDates: state.values.scheduleDates.includes("schedule-dates"),
							})}
						>
							{({ hasScheduledDates }) =>
								hasScheduledDates ? (
									<div className="flex flex-col items-center gap-4">
										<FormDatePicker id="startDate" label="Start Date" showTimeDropdown={true} />
										<FormDatePicker id="endDate" label="End Date" showTimeDropdown={true} />
										<Alert
											message={`Company timezone is set to ${companyTimezone}`}
											type="neutral"
										/>
									</div>
								) : null
							}
						</FormSubscribe>
					</div>
					<Spacer height={2} />
					<div className="flex justify-center gap-2">
						<div>
							<ButtonStandard size="large" type="button" onClick={handleClose}>
								Cancel
							</ButtonStandard>
						</div>
						<div>
							<SubmitButton color="green" size="large">
								Yes, Publish Survey
							</SubmitButton>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export const PublishSurveyInvalidModal = () => {
	const modal = useModal();
	const setters = useSetters();

	return (
		<ContentModal
			buttons={[
				<div key="publish-survey-invalid-close">
					<ButtonStandard size="large" onClick={() => setters.setModal(null)}>
						Close
					</ButtonStandard>
				</div>,
			]}
			size="small"
			handleClose={() => setters.setModal(null)}
			isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_INVALID}
		>
			<div className="flex w-full flex-col items-center gap-2">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					This survey cannot be published.
				</Typography>
				<Typography size="paragraph-2" color="neutral-9" weight="regular">
					<div className="text-center">
						At least one answerable question is required for a survey to be published
					</div>
				</Typography>
			</div>
		</ContentModal>
	);
};

export const PublishSurveySuccessModal = () => {
	const modal = useModal();
	const setters = useSetters();
	const surveyLink = useSurveyLink();

	return (
		<ContentModal
			isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_SUCCESS}
			handleClose={() => setters.setModal(null)}
		>
			<div className="flex w-full flex-col items-center gap-4">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					Your Survey Has Been Published!
				</Typography>
				<div className="w-full text-center">
					<Input value={surveyLink} />
				</div>
				<Tooltip
					key="publish-survey-success-copy-link"
					message="Survey Link Copied"
					openOnClick
					hasArrow={false}
					turnOffAfter={2000}
					placement="left"
				>
					<CopyToClipboard text={surveyLink}>
						<div className="flex h-12 items-center">
							<ButtonStandard size="large" iconLeft={faLinkSimple}>
								Copy Link
							</ButtonStandard>
						</div>
					</CopyToClipboard>
				</Tooltip>
			</div>
		</ContentModal>
	);
};
